import { useEffect, useRef, useState } from 'react';
import { bindParticlePointer } from '../lib/particlePointer';
import { isometricCubePaths } from '../lib/canvasGeometry';

const DURATION = 2500;
const clamp = value => Math.max(0, Math.min(1, value));
const random = n => { const value = Math.sin(n * 127.1 + 41.7) * 43758.5453; return value - Math.floor(value); };
const PROJECT_X = .8660254;
const BUILDINGS = [
  { x: -10, z: -9, w: 4, d: 4, h: 4, roof: true },
  { x: -2, z: -10, w: 4, d: 4, h: 13 },
  { x: 6, z: -9, w: 4, d: 5, h: 8 },
  { x: -10, z: -1, w: 5, d: 4, h: 6 },
  { x: 0, z: 0, w: 4, d: 5, h: 10 },
  { x: -8, z: 7, w: 5, d: 4, h: 3, roof: true },
  { x: 5, z: 7, w: 5, d: 4, h: 5, roof: true },
];
function makeCity() {
  const cells = [];
  const add = (x, z, y, ground, building = -1) => cells.push({ x, z, y, ground, building, seed: random(x * 17 + z * 13 + y * 31), px: (x - z) * PROJECT_X, py: (x + z) * .5 - y, dx: 0, dy: 0 });
  // Open ground and parcel edges remain visible between the buildings.
  for (let x = -12; x <= 12; x++) for (let z = -12; z <= 12; z++) {
    if (Math.abs(x) === 12 || Math.abs(z) === 12 || x === -4 || x === 4 || z === -4 || z === 4) add(x, z, -1, true);
  }
  BUILDINGS.forEach((b, index) => {
    for (let x = 0; x < b.w; x++) for (let z = 0; z < b.d; z++) {
      const roof = b.roof ? Math.min(x, b.w - 1 - x) : 0;
      const top = b.h + roof;
      for (let y = 0; y < top; y++) {
        // Only surface voxels: keep the miniature light while preserving real depth.
        if (x === 0 || x === b.w - 1 || z === 0 || z === b.d - 1 || y === top - 1) add(b.x + x, b.z + z, y, false, index);
      }
    }
  });
  return cells.sort((a, b) => a.x + a.z - b.x - b.z || a.y - b.y);
}
const CITY = makeCity();
const bounds = {
  left: Math.min(...CITY.map(c => c.px)) - 2, right: Math.max(...CITY.map(c => c.px)) + 2,
  top: Math.min(...CITY.map(c => c.py)) - 2, bottom: Math.max(...CITY.map(c => c.py)) + 2,
};
const centre = { x: (bounds.left + bounds.right) / 2, y: (bounds.top + bounds.bottom) / 2 };

export const CadastreAICityCanvas = ({ reduced = false, active = false, label }) => {
  const canvasRef = useRef(null), motion = useRef(reduced), replayRef = useRef(null), wakeRef = useRef(null);
  const [supported, setSupported] = useState(true);
  motion.current = reduced;
  useEffect(() => {
    const canvas = canvasRef.current, stage = canvas.parentElement;
    let context;
    try { context = canvas.getContext('2d'); } catch { /* Accessible static illustration fallback. */ }
    if (!context) { setSupported(false); return; }
    const cells = CITY.map(cell => ({ ...cell }));
    const style = getComputedStyle(stage), token = name => style.getPropertyValue(name).trim();
    const palette = { ink: token('--product-ink'), accent: token('--product-accent'), top: token('--product-cube-top'), left: token('--product-cube-left'), right: token('--product-cube-right') };
    const pointer = bindParticlePointer(canvas, {
      project: event => { const box = canvas.getBoundingClientRect(); return { x: event.clientX - box.left, y: event.clientY - box.top }; },
      isReduced: () => motion.current,
    });
    let width = 0, height = 0, unit = 1, dpr = 1, elapsed = 0, last = 0;
    let visible = false, started = false, disposed = false, raf = 0, resizeFrame = 0, replayCount = 0;
    let cubePaths;
    const polygon = (path, fill) => { context.fillStyle = fill; context.fill(path); };
    const drawCube = (x, y, cell, opacity) => {
      context.setTransform(dpr, 0, 0, dpr, x * dpr, y * dpr);
      context.globalAlpha = opacity;
      const { left, right, top } = cubePaths;
      polygon(left, cell.ground || cell.seed > .88 ? palette.accent : palette.ink);
      polygon(left, palette.left);
      polygon(right, palette.ink); polygon(right, palette.right);
      polygon(top, palette.accent); polygon(top, palette.top);
    };
    function draw(now, delta = 0) {
      if (!width || !height) return false;
      const reduce = motion.current;
      if (reduce) elapsed = DURATION;
      else if (started) elapsed = Math.min(DURATION, elapsed + delta);
      const progress = reduce ? 1 : elapsed / DURATION;
      const contact = pointer.read(now), interactive = !reduce && contact.active;
      const radius = Math.min(width * .3, 110), blend = reduce ? 1 : 1 - Math.exp(-Math.max(delta, 16) * .01);
      let displacement = 0;
      context.setTransform(dpr, 0, 0, dpr, 0, 0); context.clearRect(0, 0, width, height);
      for (const cell of cells) {
        const delay = cell.ground ? cell.seed * .12 : .23 + cell.y / 13 * .46 + cell.seed * .07;
        const local = reduce ? 1 : clamp((progress - delay) / (cell.ground ? .19 : .24));
        if (local === 0) continue;
        const ease = 1 - (1 - local) ** 3;
        const bx = width / 2 + (cell.px - centre.x) * unit;
        const by = height / 2 + (cell.py - centre.y) * unit;
        const vx = bx - contact.x, vy = by - contact.y, distance = interactive ? Math.hypot(vx, vy) || 1 : 1;
        const force = interactive ? Math.max(0, 1 - distance / radius) ** 2 * unit * 2.6 : 0;
        cell.dx += ((reduce ? 0 : vx / distance * force) - cell.dx) * blend;
        cell.dy += ((reduce ? 0 : vy / distance * force) - cell.dy) * blend;
        displacement = Math.max(displacement, Math.hypot(cell.dx, cell.dy));
        const flight = (1 - ease) * unit;
        const x = bx + (cell.seed - .5) * flight * 8 + cell.dx;
        const y = by - flight * (8 + cell.seed * 8) + cell.dy;
        drawCube(x, y, cell, local * (cell.ground ? .25 : .97));
      }
      context.globalAlpha = 1;
      canvas.dataset.ready = 'true'; canvas.dataset.state = reduce ? 'static' : !started ? 'waiting' : progress < 1 ? 'assembling' : 'assembled';
      canvas.dataset.build = progress.toFixed(3); canvas.dataset.displacement = displacement.toFixed(2);
      canvas.dataset.particleCount = String(cells.length); canvas.dataset.buildingCount = String(BUILDINGS.length);
      canvas.dataset.pointerActive = String(interactive); canvas.dataset.input = contact.input; canvas.dataset.contact = String(contact.contact); canvas.dataset.lastEnd = contact.lastEnd; canvas.dataset.replayCount = String(replayCount);
      return !reduce && (started && progress < 1 || interactive || displacement > .05);
    }
    function frame(now) {
      raf = 0;
      if (disposed || !visible || document.hidden) return;
      if (last && now - last < 32) { raf = requestAnimationFrame(frame); return; }
      const delta = Math.min(70, last ? now - last : 16); last = now;
      if (draw(now, delta)) raf = requestAnimationFrame(frame);
    }
    function wake() { if (!disposed && visible && !document.hidden && !raf) { last = 0; raf = requestAnimationFrame(frame); } }
    const resize = () => {
      if (disposed) return;
      width = stage.clientWidth; height = stage.clientHeight; dpr = Math.min(devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr);
      unit = Math.min(width / (bounds.right - bounds.left + 5), height / (bounds.bottom - bounds.top + 5));
      cubePaths = isometricCubePaths(unit * .88, PROJECT_X);
      draw(performance.now()); wake();
    };
    const resizeObserver = new ResizeObserver(() => { cancelAnimationFrame(resizeFrame); resizeFrame = requestAnimationFrame(resize); });
    resizeObserver.observe(stage); resize();
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0]; visible = entry.isIntersecting;
      if (entry.intersectionRatio >= .4 && !started) { started = true; elapsed = 0; }
      if (visible) wake();
      else { cancelAnimationFrame(raf); raf = 0; last = 0; started = false; elapsed = 0; pointer.reset('offscreen'); }
    }, { threshold: [0, .4] });
    observer.observe(stage);
    const visibilityChange = () => { if (document.hidden) { cancelAnimationFrame(raf); raf = 0; } else wake(); };
    document.addEventListener('visibilitychange', visibilityChange);
    const events = ['pointermove', 'pointerdown', 'pointerup', 'pointerleave', 'pointercancel'];
    events.forEach(event => canvas.addEventListener(event, wake, { passive: true }));
    replayRef.current = () => {
      if (motion.current) return;
      elapsed = 0; started = true; replayCount++;
      pointer.reset('replay'); cells.forEach(cell => { cell.dx = 0; cell.dy = 0; }); draw(performance.now()); wake();
    };
    wakeRef.current = () => { draw(performance.now()); wake(); };
    return () => {
      disposed = true; cancelAnimationFrame(raf); cancelAnimationFrame(resizeFrame);
      observer.disconnect(); resizeObserver.disconnect(); pointer.dispose();
      events.forEach(event => canvas.removeEventListener(event, wake));
      document.removeEventListener('visibilitychange', visibilityChange);
      replayRef.current = null; wakeRef.current = null;
    };
  }, []);
  useEffect(() => { wakeRef.current?.(); }, [reduced]);
  useEffect(() => { if (active && !reduced) replayRef.current?.(); }, [active, reduced]);
  return <span className="court-ai-scales cadastre-ai-city" data-testid="cadastre-ai-city-figure">
    <span className="court-ai-scales-stage">
      <canvas ref={canvasRef} hidden={!supported} role="img" aria-label={label} data-testid="cadastre-ai-tile-canvas" />
      {!supported && <svg className="court-ai-scales-fallback" viewBox={`${bounds.left - 2} ${bounds.top - 2} ${bounds.right - bounds.left + 4} ${bounds.bottom - bounds.top + 4}`} role="img" aria-label={label} data-testid="cadastre-ai-city-fallback">{CITY.map((cell, i) => <g key={i} transform={`translate(${cell.px} ${cell.py})`} opacity={cell.ground ? .25 : 1}><path d="M-.76,-.44 L0,0 L0,.88 L-.76,.44Z" fill="var(--product-ink)" /><path d="M0,0 L.76,-.44 L.76,.44 L0,.88Z" fill="var(--product-ink)" /><path d="M0,-.88 L.76,-.44 L0,0 L-.76,-.44Z" fill="var(--product-accent)" /></g>)}</svg>}
    </span>
  </span>;
};
